import React, { useState, useEffect } from 'react';
import { Phone, MapPin, Clock, ChevronRight, Menu, X, CheckCircle, Award, Users, Star, Instagram, Facebook, MessageCircle, Link } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const whatsappNumber = '5511991706670';
  const whatsappMessage = encodeURIComponent('Olá, gostaria de agendar uma avaliação gratuita');
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  // Números adicionais de contato
  const telefoneFixo = '(11) 3721-3027';
  const whatsappDr = '(11) 99170-6670';
  const whatsappClinica = '(11) 97220-2311';
  const whatsappFatima = '(11) 95046-4354';

 const beforeAfterCases = [
  {
    before: '/antes-1.jpeg',
    after: '/depois-1.jpeg',
    title: 'Volte a sorrir com confiança'
  },
  {
    before: '/antes-2.jpeg',
    after: '/depois-2.jpeg',
    title: 'Autoestima renovada através do sorriso'
  },
  {
    before: '/antes-3.jpeg',
    after: '/depois-3.jpeg',
    title: 'Seu novo sorriso em poucos dias'
  },
  {
    before: '/antes-4.jpeg',
    after: '/depois-4.jpeg',
    title: 'Redescubra o prazer de sorrir sem medo'
  },
  {
    before: '/antes-5.jpeg',
    after: '/depois-5.jpeg',
    title: 'Uma nova fase, um novo sorriso'
  },
  {
    before: '/antes-6.jpeg',
    after: '/depois-6.jpeg',
    title: 'Casamento recuperado com sorriso perfeito'
  }
];

  const testimonials = [
    {
      name: 'Maria Silva',
      text: 'Recuperei minha autoestima e voltei a sorrir com confiança. O Dr. Ricardo e toda equipe foram incríveis!',
      rating: 5
    },
    {
      name: 'João Santos',
      text: 'Atendimento impecável e resultado incrível em 24h. Não acreditei quando vi meu novo sorriso.',
      rating: 5
    },
    {
      name: 'Ana Rodrigues',
      text: 'Dr. Ricardo e equipe transformaram meu sorriso e minha vida. Profissionalismo e cuidado incomparáveis.',
      rating: 5
    },
    {
      name: 'Carlos Mendes',
      text: 'Tecnologia de ponta e atendimento humanizado. Voltei a sorrir sem medo em apenas um dia!',
      rating: 5
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="home-container">
      {/* Navigation */}
      <nav className="navigation-header">
        <div className="container">
          <div className="nav-content">
            <div className="navigation-logo">
              <img
                src="https://customer-assets.emergentagent.com/job_rd-odonto-implantes/artifacts/bc7h17h1_RD-ODONTO-DOURADO-removebg-preview.png"
                alt="RD Odontologia"
                className="logo-img"
              />
            </div>

            <ul className={`navigation-menu ${mobileMenuOpen ? 'mobile-open' : ''}`}>
              <li><button onClick={() => scrollToSection('hero')} className="navigation-link">Início</button></li>
              <li><button onClick={() => scrollToSection('sobre')} className="navigation-link">Sobre</button></li>
              <li><button onClick={() => scrollToSection('especialista')} className="navigation-link">Especialista</button></li>
              <li><button onClick={() => scrollToSection('protocolo')} className="navigation-link">Tratamentos</button></li>
              <li><button onClick={() => scrollToSection('contato')} className="navigation-link">Contato</button></li>
            </ul>

            <div className="navigation-utilities">
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="whatsapp-header-link">
                <Phone size={18} />
                <span>{whatsappDr}</span>
              </a>
              <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-content">
              <h1 className="hero-title">
                Recupere o seu sorriso em <span className="text-[#C9A14A]">24/72 horas</span> com o Protocolo de Carga Imediata
              </h1>
              <p className="hero-subtitle">
                Implantes dentários de alto padrão, estética natural e resultados imediatos.
              </p>

              <div className="hero-stats">
                <div className="stat-card">
                  <Award className="stat-icon" />
                  <div>
                    <div className="stat-number">+20 anos</div>
                    <div className="stat-label">de experiência</div>
                  </div>
                </div>
                <div className="stat-card">
                  <Users className="stat-icon" />
                  <div>
                    <div className="stat-number">+2.500</div>
                    <div className="stat-label">implantes realizados</div>
                  </div>
                </div>
                <div className="stat-card">
                  <Star className="stat-icon" />
                  <div>
                    <div className="stat-number">100%</div>
                    <div className="stat-label">satisfação</div>
                  </div>
                </div>
              </div>

              <div className="hero-cta">
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-primary">
                  AGENDAR MINHA AVALIAÇÃO
                  <ChevronRight size={18} />
                </a>
                <div className="hero-contact">
                  <Phone size={16} />
                  <span>WhatsApp: {whatsappDr}</span>
                </div>
              </div>
            </div>

            <div className="hero-image-wrapper">
              <img 
                src="https://customer-assets.emergentagent.com/job_rd-odonto-implantes/artifacts/01mpobd2_RICARDO-SEMFUNDO.png" 
                alt="Dr. Ricardo - Especialista em Implantodontia"
                className="hero-photo-fullwidth"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Sobre Section */}
      <section id="sobre" className="sobre-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Excelência em Implantodontia e Estética Avançada</h2>
          </div>

          <div className="sobre-content">
            <div className="sobre-text">
              <p className="body-large">
                Na <strong>R D Odontologia Especializada</strong>, acreditamos que um sorriso saudável transforma vidas.
              </p>
              <p className="body-regular">
                Utilizamos <strong>tecnologia digital 3D</strong> e técnicas exclusivas de <strong>Implantodontia de Carga Imediata</strong>,
                que permitem reabilitar o sorriso com dentes fixos e naturais em até <strong>24/72 horas</strong>, com conforto, precisão e segurança.
              </p>
            </div>

            <div className="sobre-image">
              <img
                src="https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=800&q=80"
                alt="Clínica Moderna"
                className="content-image"
              />
            </div>
          </div>

          <div className="locations-grid">
            <div className="location-card">
              <MapPin className="location-icon" />
              <div>
                <h3 className="location-title">Butantã - São Paulo</h3>
                <p className="location-address">Rua Francisco Perrotti, 633F</p>
                <p className="location-cep">CEP: 05314-001 - São Paulo, SP</p>
              </div>
            </div>
          </div>

          <div className="section-cta">
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-primary">
              AGENDAR AVALIAÇÃO GRATUITA
              <ChevronRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* Motivos Section */}
      <section id="motivos" className="motivos-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Por que escolher a R D Odontologia?</h2>
          </div>

          <div className="motivos-grid">
            <div className="motivo-card">
              <div className="motivo-icon">
                <Clock size={32} />
              </div>
              <h3 className="motivo-title">Resultados em 24h</h3>
              <p className="motivo-text">Volte a sorrir, falar e mastigar no mesmo dia com dentes fixos imediatos.</p>
            </div>

            <div className="motivo-card">
              <div className="motivo-icon">
                <Award size={32} />
              </div>
              <h3 className="motivo-title">Tecnologia Digital 3D</h3>
              <p className="motivo-text">Planejamento preciso e personalizado com as mais avançadas ferramentas.</p>
            </div>

            <div className="motivo-card">
              <div className="motivo-icon">
                <Star size={32} />
              </div>
              <h3 className="motivo-title">Conforto e Estética Premium</h3>
              <p className="motivo-text">Dentes fixos com aparência natural e conforto incomparável.</p>
            </div>

            <div className="motivo-card">
              <div className="motivo-icon">
                <Users size={32} />
              </div>
              <h3 className="motivo-title">Experiência e Confiança</h3>
              <p className="motivo-text">Mais de 2.500 implantes realizados com excelência e segurança.</p>
            </div>

            <div className="motivo-card">
              <div className="motivo-icon">
                <CheckCircle size={32} />
              </div>
              <h3 className="motivo-title">Acompanhamento Humanizado</h3>
              <p className="motivo-text">Atendimento próximo e suporte completo em todas as etapas.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Especialista Section */}
      <section id="especialista" className="especialista-section">
        <div className="container">
          <div className="especialista-grid">
            <div className="especialista-image">
              <img
                src="https://customer-assets.emergentagent.com/job_2df67b13-3db1-46e1-b451-454c5a7c020b/artifacts/6rcjv8cq_Gemini_Generated_Image_rpdjnnrpdjnnrpdj.png"
                alt="Dr. Ricardo"
                className="doctor-photo"
              />
            </div>
            <div className="especialista-content">
              <h2 className="section-title">Dr. Ricardo</h2>
              <p className="especialista-subtitle">Especialista em Implantodontia e Reabilitação Oral</p>
              <p className="body-large">
                Reconhecido por sua precisão técnica e abordagem acolhedora, o Dr. Ricardo é referência em
                <strong> tratamentos de alta complexidade</strong>.
              </p>
              <p className="body-regular">
                Com foco na <strong>estética, funcionalidade e tecnologia</strong>, devolve não apenas sorrisos,
                mas <strong>autoconfiança e qualidade de vida</strong> aos seus pacientes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Protocolo Section */}
      <section id="protocolo" className="protocolo-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Como funciona o Protocolo de Carga Imediata</h2>
          </div>

          <div className="protocolo-grid">
            <div className="protocolo-step">
              <div className="step-number">01</div>
              <h3 className="step-title">Consulta de Avaliação Personalizada</h3>
              <p className="step-text">Diagnóstico completo e exames digitais para planejamento preciso.</p>
            </div>

            <div className="protocolo-step">
              <div className="step-number">02</div>
              <h3 className="step-title">Planejamento Digital 3D</h3>
              <p className="step-text">Simulação virtual do novo sorriso com tecnologia de ponta.</p>
            </div>

            <div className="protocolo-step">
              <div className="step-number">03</div>
              <h3 className="step-title">Implantes e Dentes Fixos em 24h</h3>
              <p className="step-text">Cirurgia segura e resultado imediato no mesmo dia.</p>
            </div>

            <div className="protocolo-step">
              <div className="step-number">04</div>
              <h3 className="step-title">Acompanhamento e Manutenção Premium</h3>
              <p className="step-text">Suporte contínuo e cuidado pós-operatório especializado.</p>
            </div>
          </div>

          <div className="section-cta">
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-primary">
              FALAR PELO WHATSAPP
              <ChevronRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* Before After Section */}
      <section id="antes-depois" className="antes-depois-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Transformações Reais</h2>
            <p className="section-subtitle">Arraste o slider para ver o antes e depois dos nossos tratamentos</p>
          </div>

          <div className="antes-depois-grid">
            {beforeAfterCases.map((caseItem, index) => (
              <BeforeAfterSlider
                key={index}
                beforeImage={caseItem.before}
                afterImage={caseItem.after}
                title={caseItem.title}
              />
            ))}
          </div>

          <div className="section-cta">
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-primary">
              QUERO MEU NOVO SORRISO
              <ChevronRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* Resultados Section */}
      <section id="resultados" className="resultados-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">O que dizem nossos pacientes</h2>
          </div>

          <div className="testimonials-carousel">
            <div className="testimonial-card">
              <div className="testimonial-stars">
                {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                  <Star key={i} size={20} fill="#C9A14A" color="#C9A14A" />
                ))}
              </div>
              <p className="testimonial-text">"{testimonials[activeTestimonial].text}"</p>
              <p className="testimonial-author">— {testimonials[activeTestimonial].name}</p>
            </div>

            <div className="testimonial-dots">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  className={`testimonial-dot ${index === activeTestimonial ? 'active' : ''}`}
                  onClick={() => setActiveTestimonial(index)}
                  aria-label={`Ver depoimento ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Perguntas Frequentes</h2>
          </div>

          <Accordion type="single" collapsible className="faq-accordion">
            <AccordionItem value="item-1">
              <AccordionTrigger>O que é o Protocolo de Carga Imediata?</AccordionTrigger>
              <AccordionContent>
                É uma técnica moderna que permite a colocação de dentes fixos no mesmo dia da cirurgia de implante.
                Com planejamento digital 3D, instalamos os implantes e já colocamos uma prótese provisória fixa,
                permitindo que você saia da clínica sorrindo no mesmo dia.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2">
              <AccordionTrigger>O procedimento é doloroso?</AccordionTrigger>
              <AccordionContent>
                Não. O procedimento é realizado sob anestesia local e utilizamos técnicas minimamente invasivas.
                A maioria dos pacientes relata sentir apenas um leve desconforto após a cirurgia, facilmente
                controlado com medicação prescrita.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3">
              <AccordionTrigger>Quanto tempo dura o tratamento?</AccordionTrigger>
              <AccordionContent>
                Em até 24/72 horas, você já sai com dentes fixos provisórios. Após o período de osseointegração
                (3 a 6 meses), instalamos a prótese definitiva. Durante todo o processo, você permanece com
                dentes fixos e funcionais.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4">
              <AccordionTrigger>Vocês atendem convênios?</AccordionTrigger>
              <AccordionContent>
                Não trabalhamos com convênios odontológicos. Oferecemos condições exclusivas de pagamento e
                parcelamentos acessíveis, permitindo que você realize seu tratamento com a qualidade premium
                que merece.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5">
              <AccordionTrigger>Os implantes são permanentes?</AccordionTrigger>
              <AccordionContent>
                Sim, os implantes dentários são projetados para serem permanentes. Com manutenção adequada,
                higiene bucal correta e acompanhamento regular, os implantes podem durar toda a vida.
                A taxa de sucesso é superior a 95%.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Mapa Section */}
      <section id="mapa" className="mapa-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Nossa Localização</h2>
          </div>

          <div className="mapa-single">
            <div className="mapa-card">
              <h3 className="location-title">Butantã - São Paulo</h3>
              <p className="location-address">Rua Francisco Perrotti, 633F</p>
              <p className="location-cep">CEP: 05314-001</p>
              <div className="mapa-embed">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3657.8!2d-46.7234!3d-23.5614!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94ce5673c0d6e8b9%3A0x0!2sRua%20Francisco%20Perrotti%2C%20633F%20-%20Butant%C3%A3%2C%20S%C3%A3o%20Paulo!5e0!3m2!1spt-BR!2sbr!4v1234567890"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mapa Butantã"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final Section */}
      <section id="contato" className="cta-final-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Agende agora sua avaliação</h2>
            <p className="cta-text">
              Descubra como o seu novo sorriso pode transformar sua vida.<br />
              Atendimento exclusivo, tecnologia de ponta e resultados imediatos.
            </p>
            <p className="cta-payment">Aceitamos cartões de crédito e boleto bancário.</p>

            <div className="cta-actions">
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-primary-large">
                <Phone size={24} />
                AGENDAR MINHA AVALIAÇÃO
              </a>
              <p className="cta-phone">
                <MessageCircle size={20} />
                WhatsApp: {whatsappDr}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <img
                src="https://customer-assets.emergentagent.com/job_rd-odonto-implantes/artifacts/bc7h17h1_RD-ODONTO-DOURADO-removebg-preview.png"
                alt="RD Odontologia"
                className="footer-logo"
              />
              <p className="footer-tagline">Excelência em Implantodontia e Estética Avançada</p>
            </div>

            <div className="footer-links">
              <h4 className="footer-title">Menu</h4>
              <ul className="footer-menu">
                <li><button onClick={() => scrollToSection('hero')}>Início</button></li>
                <li><button onClick={() => scrollToSection('sobre')}>Sobre</button></li>
                <li><button onClick={() => scrollToSection('especialista')}>Especialista</button></li>
                <li><button onClick={() => scrollToSection('protocolo')}>Tratamentos</button></li>
                <li><button onClick={() => scrollToSection('contato')}>Contato</button></li>
              </ul>
            </div>

            <div className="footer-contact">
              <h4 className="footer-title">Contato</h4>
              <div className="footer-info">
                <Phone size={16} />
                <span>Fixo: {telefoneFixo}</span>
              </div>
              <div className="footer-info" style={{ marginTop: '8px' }}>
                <MessageCircle size={16} />
                <span>WhatsApp Dr: {whatsappDr}</span>
              </div>
              <div className="footer-info" style={{ marginTop: '8px' }}>
                <MessageCircle size={16} />
                <span>WhatsApp: {whatsappClinica}</span>
              </div>
              <div className="footer-info" style={{ marginTop: '8px' }}>
                <MessageCircle size={16} />
                <span>Fátima: {whatsappFatima}</span>
              </div>
              <div className="footer-locations" style={{ marginTop: '16px' }}>
                <p><strong>Butantã:</strong> Rua Francisco Perrotti, 633F - CEP: 05314-001</p>
              </div>
            </div>

            <div className="footer-social">
              <h4 className="footer-title">Redes Sociais</h4>
              <div className="social-icons">
                <a href="https://linkbioc.my.canva.site/rd-odontologia" target="_blank" rel="noopener noreferrer" aria-label="Link Bio">
                  <Link size={24} />
                </a>
                <a href="https://www.facebook.com/RDOdontologia" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <Facebook size={24} />
                </a>
                <a href="https://instagram.com/rdodontologia" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <Instagram size={24} />
                </a>
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                  <MessageCircle size={24} />
                </a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>© 2025 R D Odontologia Especializada - Todos os direitos reservados.</p>
            <p className="footer-privacy">
              <button>Política de Privacidade</button> | <button>Termos de Uso</button>
            </p>
            <br></br>
            <div className="developer-credit">
              <span className="developer-text text-md">Desenvolvido por </span>
              <a
                href="https://www.cass-io.com"
                target="_blank"
                rel="noopener noreferrer"
                className="developer-link text-lg"
              >
                <strong>Cass.io</strong>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="whatsapp-float" aria-label="WhatsApp">
        <MessageCircle size={28} />
      </a>
    </div>
  );
};

export default Home;